'use client';

import React, { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';
import AppLogo from '@/components/ui/AppLogo';
import { APP_CONFIG } from '@/lib/appConfig';
import LoginForm from './LoginForm';
import SignupForm from './SignupForm';
import AuthVisualPanel from './AuthVisualPanel';

export default function AuthScreen() {
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>('login');
  const [appName, setAppName] = useState(APP_CONFIG?.appName || 'NEUROMINER');
  const [logoUrl, setLogoUrl] = useState('/assets/images/app_logo.png');

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from('app_settings')
      .select('key, value')
      .in('key', ['app_name', 'login_logo_url', 'logo_url'])
      .then(({ data }) => {
        if (!data) return;
        const map: Record<string, string> = {};
        data.forEach((row: any) => { map[row.key] = row.value; });
        if (map.app_name) setAppName(map.app_name);
        if (map.login_logo_url) setLogoUrl(map.login_logo_url);
        else if (map.logo_url) setLogoUrl(map.logo_url);
      });
  }, []);

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Visual Panel — hidden on mobile */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden">
        <AuthVisualPanel appName={appName} />
      </div>

      {/* Right Form Panel */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex flex-col min-h-screen overflow-y-auto">
        {/* Mobile Header with dynamic logo */}
        <div className="lg:hidden flex flex-col items-center gap-3 px-6 pt-10 pb-4">
          <AppLogo src={logoUrl} size={56} />
          <span className="font-extrabold text-xl text-foreground tracking-tight">{appName}</span>
        </div>

        <div className="flex-1 flex flex-col justify-center px-6 py-8 lg:px-10 xl:px-14 max-w-lg mx-auto w-full">
          {/* Desktop Logo — dynamic */}
          <div className="hidden lg:flex flex-col items-center gap-3 mb-8">
            <AppLogo src={logoUrl} size={64} />
            <span className="font-extrabold text-2xl text-foreground tracking-tight">{appName}</span>
          </div>

          {/* Tab Switcher */}
          <div className="flex bg-muted rounded-2xl p-1 mb-8">
            <button
              onClick={() => setActiveTab('login')}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                activeTab === 'login' ? 'bg-card card-shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setActiveTab('signup')}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                activeTab === 'signup' ? 'bg-card card-shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Create Account
            </button>
          </div>

          {/* Form */}
          {activeTab === 'login' ? (
            <LoginForm onSwitchToSignup={() => setActiveTab('signup')} />
          ) : (
            <SignupForm onSwitchToLogin={() => setActiveTab('login')} />
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 text-center">
          <p className="text-xs text-muted-foreground">
            © 2026 {appName}.
          </p>
        </div>
      </div>
    </div>
  );
}